import { Component, OnInit, OnDestroy, OnChanges, AfterViewInit, SimpleChanges, Input, Output, EventEmitter, ContentChild, TemplateRef, ElementRef, Renderer2, ViewChild, forwardRef, Optional, Host, HostListener, HostBinding, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Subscription, fromEvent, Subject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as _ from 'underscore';
import { VirtualKeyboardComponent } from '@otas/utils/custom-components/virtual-keyboard/virtual-keyboard.component';
import { AutoSuggestOption, AutoSuggestOptionGroup } from '@otas/utils/custom-components/auto-suggest/auto-suggest.component';
import { IAutoCompleteSettings } from './auto-complete.module';
import { AUTO_COMPLETE_DEFAULT_SETTINGS } from './auto-complete.defaults';
import { DropdownItemDirective } from './directives/dropdown-item.directive';
import { DropdownContainerComponent } from './dropdown-container/dropdown-container.component';
import { EnterKeyListenerDirective } from '@otas/utils/directives/helper/enter.directive';
import { OverlayPanelComponent } from '@otas/utils/custom-components/overlay-panel/overlay-panel.component';
@Component({
    selector: 'app-auto-complete',
    templateUrl: './auto-complete.component.html',
    styleUrls: ['./auto-complete.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutoCompleteComponent),
            multi: true
        }
    ],
    standalone: false
})
export class AutoCompleteComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor {

  @Input() options: AutoSuggestOption[] = [];
  inputValue = new UntypedFormControl('');
  activeOption: AutoSuggestOption;
  @Input() optionsGroup: AutoSuggestOptionGroup[] = [];
  @Output() change = new EventEmitter();
  @Output() keydown = new EventEmitter<KeyboardEvent>();
  @Output() afterhide = new EventEmitter();
  @ContentChild(TemplateRef, { read: TemplateRef, static: false }) footerDetails;
  visible = false;
  visibleSubject = new Subject<boolean>();
  @Input() suggestion = false;
  @Input() @HostBinding('tabindex') tabindex = -1;
  defaultSettings: IAutoCompleteSettings = AUTO_COMPLETE_DEFAULT_SETTINGS;
  @Input() settings: IAutoCompleteSettings = AUTO_COMPLETE_DEFAULT_SETTINGS;

  @Input() isClearAll = false;
  @ViewChild(VirtualKeyboardComponent, { static: true }) virtualKeyBoardRef: VirtualKeyboardComponent;
  @ViewChild('divEl', { static: true }) divElRef: ElementRef;
  @ViewChild('dropDown', { static: true }) dropDownContainer;
  footerDetailsVisible = true;
  visiblSubscription: Subscription;
  private _subs: Subscription[] = [];
  @Input() filterCount = 2;
  blured = new EventEmitter();
  isSpace: boolean;
  isFocused: boolean;
  dynamicList: any;
  constructor(private _elementRef: ElementRef, private renderer: Renderer2,
    @Optional() @Host() private enterKey: EnterKeyListenerDirective) {
  }
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };
  registerOnChange(fn: (value: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  writeValue(obj: any): void {
    if (!obj) {
      this.inputValue.setValue('');
      this.activeOption = null;
      return;
    }
    const dropdownComponent = this.dropDownContainer as DropdownContainerComponent;
    const options = dropdownComponent?.dropdownItems;
    if (options?.length) {
      const option = options.find((op: DropdownItemDirective) => op.item.id === obj);
      if (option) {
        this.isFocused = false;
        this.onChange(option.id);
        this.change.emit(option);
        this.activeOption = option.item;
        this.inputValue.setValue(option.item.name);
        this.visibleSubject.next(false);
      }
    }
    if (!this.settings.isSuggestion) {
      this.isFocused = false;
      this.onChange(obj);
      this.change.emit(obj);
      this.inputValue.setValue(obj);
      this.visibleSubject.next(false);
    }
  }
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.inputValue.disable() : this.inputValue.enable();
    const nativeElement = this._elementRef.nativeElement;
    this.renderer.setAttribute(nativeElement, 'ng-reflect-is-disable', String(isDisabled));
  }
  ngOnInit(): void {
    this.visiblSubscription = this.visibleSubject.subscribe((value: boolean) => {
      this.visible = value;
    });
    
    this._subs.push(
      this.afterhide.subscribe(hide => {
        const overlay: OverlayPanelComponent = this.virtualKeyBoardRef.keyBoardPanel;
        if (hide === 'close') {
          this.isFocused && this.afterBlur();
          this.isFocused = false;
        }
        overlay?.visible && overlay.hide();
      })
    );
  }
  ngAfterViewInit() {
    setTimeout(() => {
      this.dropDownContainer.afterFilter.subscribe((data) => {
        this.footerDetailsVisible = !!data.length;
      })
    }, 50);
    this.inputValue.valueChanges.pipe(tap(inputValue => {
      if (!isNaN(inputValue) && inputValue.length && _.contains(this.settings.searchBy, 'prefix')) {
        this.setNullValue();
      }
      if (!this.settings.isSuggestion) {
        this.isFocused = false;
        this.onChange(inputValue);
        if (!inputValue) {
          this.visibleSubject.next(false);
        }
      }
    })).subscribe((inputValue) => {

      if (!inputValue) {
        this.setNullValue();
        if (this.settings.isDynamicData) { this.visibleSubject.next(false); }
      } else {
        if (document.activeElement === this.virtualKeyBoardRef.inputField.elementRef.nativeElement) {
          if (inputValue.length === 1) {
            this.activeOption = null;
          }
          if (this.settings.isDynamicData && !!inputValue.trim()) {
            const keyboard = this.virtualKeyBoardRef.inputField.keyboard;
            const isMalayamOrManglish = _.contains(['malayalam', 'manglish'], keyboard);
            const mals = ['\u0d4a',
              '\u0d43',
              '\u0d4c',
              '\u0d48',
              '\u0d3e',
              '\u0d40',
              '\u0d42',
              '\u0d4b',
              '\u0d47',
              '\u0d4d',
              '\u0d3f',
              '\u0d41',
              '\u0d46',
              '\u0d02',
              '\u0d4a',
              '\u0d03',
              '\u0d57']
            const name = (isMalayamOrManglish && inputValue.length === 2 && mals.includes(inputValue.substring(1, 2))) ? inputValue.substring(0, 1) : inputValue.trim();

            if (name.length === 1 || this.filterCount < 0) {
              this.settings.dataToFetch(name).subscribe((response) => {
                this.dynamicList = response;
                if (response && response.length && inputValue.substring(1, 2) !== '\u0d4d') {
                  this.options = this.dynamicList.filter(e => (e.name as string).toLowerCase()[this.settings.searchFn](inputValue.toLowerCase())).slice(0, 250);
                } else {
                  this.options = this.dynamicList?.slice(0, 250);
                }

              })
            } else {
              if (!!this.dynamicList) {
                this.options = this.dynamicList.filter(e => (e.name as string).toLowerCase()[this.settings.searchFn](inputValue.toLowerCase())).slice(0, 250);
              }

            }

          }
          this.show(this.isSpace);
        }
      }
    });
    const node = this.virtualKeyBoardRef.inputField.elementRef.nativeElement;
    fromEvent(node, 'blur').subscribe(() => {
        this.hide(); // Ensure dropdown hides
    });
    this.onInputClicked();
    this.inputFocusListener()
  }
  private inputFocusListener(): void {
    const node = this.virtualKeyBoardRef.inputField.elementRef.nativeElement;
    this._subs.push(
      fromEvent(node, 'focus').subscribe(() => {
        this.isFocused = true;
        this.onInputFieldFocused();
      })
    );
  }
  private onInputClicked(): void {
    const node = this.virtualKeyBoardRef.inputField.elementRef.nativeElement;
    const nativeElement = this._elementRef.nativeElement;
    const tagName = nativeElement.tagName;
    
    this._subs.push(
      fromEvent(node, 'click').subscribe((event: MouseEvent) => {
        const isOutsideComponent = (event.target as HTMLElement).closest(tagName) !== nativeElement;
        if (!isOutsideComponent) {
          event.preventDefault();
          event.stopImmediatePropagation(); // Sufficient, removed redundant stopPropagation
          this.settings.selectionOnclick && this.onInputFieldFocused();
        }
      })
    );
  }
  
  public onInputFieldFocused(): void {
    const node = this.virtualKeyBoardRef.inputField.elementRef.nativeElement;
    if (this.settings.needSelection && this.inputValue.value) {
      const value = this.inputValue.value;
      node.value = value;
      node.setSelectionRange(0, value.length);
    }
  }
  setNullValue() {
    this.onChange(null);
    this.change.emit(null);
    this.activeOption = null;
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.settings = { ...this.defaultSettings, ...this.settings };
    if (this.optionsGroup?.length) {
      const groupedOptions = this.optionsGroup.flatMap(group => group.options);
      this.options.push(...groupedOptions);
    }
  }
  @HostListener('focus')
  onfocus(): void {
    this.virtualKeyBoardRef.inputField.elementRef.nativeElement?.focus();
  }
  ngOnDestroy() {
    if (this.visiblSubscription) {
      this.visiblSubscription.unsubscribe();
    }
  }
  closeDropDown = (): void => {
    this.visibleSubject.next(false);
    this.afterhide.emit('close');
  }
  setvalue(selectedOption: AutoSuggestOption): void {
    this.activeOption = selectedOption;
    this.change.emit(selectedOption);
    this.isFocused = false;
    this.onChange(selectedOption.id);
    this.inputValue.setValue(selectedOption.id ? selectedOption.name : '');
    this.isSpace = false;
    this.hide();
  }
  trackByFn(index: number, item: AutoSuggestOption): string | number {
    return item ? item.name : index;
  }
  setSelectedValues(selectedOption: DropdownItemDirective): void {
    this.change.emit(selectedOption);
    this.isFocused = false;
    const { id, name } = selectedOption.item;
    if (!this.settings.isSuggestion) {
      this.onChange(name);
      this.inputValue.setValue(name);
    } else {
      this.onChange(selectedOption.id);
      this.activeOption = selectedOption.item;
      this.inputValue.setValue(selectedOption.id ? name : '');
    }
  }
  afterBlur() {
    const childInput = this.virtualKeyBoardRef.inputField.elementRef.nativeElement;
    const options = (this.dropDownContainer as DropdownContainerComponent).dropdownItems;
    if (document.activeElement !== childInput) {
      if (!!childInput.value) {
        let obj = _.find(options, option => this.match(option.item, '==='));
        if (!this.settings.isSuggestion) {
          obj = _.find(options, option => this.match(option.item, '==='));
        } else {
          obj = _.find(options, option => this.match(option.item, 'startsWith'));
        }
        const selectedOption = obj ? obj : { item: { id: childInput.value, name: childInput.value } };
        const value = this.settings.isSuggestion && this.activeOption ? Object.assign(this.activeOption, { item: { id: this.activeOption.id, name: this.activeOption.name } }) : selectedOption;
        this.setSelectedValues(value);
      } else {
        this.setNullValue();
      }

    }
    this.isSpace = false;
  }
  private match(option: any, searchFn: string): boolean {
    const searchKeys: string[] = this.settings.searchBy;
    const searchValue = this.inputValue.value.toLowerCase();
    return searchKeys.some(key => {
      const fieldValue = option[key]?.toLowerCase();
      if (!fieldValue) return false;
      return searchFn === '===' 
        ? fieldValue === searchValue 
        : fieldValue[searchFn](searchValue);
    });
  }
  show(isSpace: boolean = false): void {
    this.isSpace = isSpace;
    this.visibleSubject.next(true);
    requestAnimationFrame(() => {
      const dropdownEl = this._elementRef.nativeElement.querySelector('.dropdown-menu');
      if (!dropdownEl) return; // Safety check
      this.renderer.addClass(dropdownEl, 'show');
      this.renderer.setStyle(dropdownEl, 'width', '100%'); // Fixed spacing
      this.renderer.setStyle(dropdownEl, 'max-height', '265px');
      this.setPosition();
    });
  }
  hide() {
    this.visibleSubject.next(false);
    this.afterhide.emit('hide');
  }
  focusFirstElementInForm(timeout: number = 100): void {
    this.enterKey?.focusFirstElement(timeout);
  }
  private getOffset(el: HTMLElement): { top: number; left: number } {
    const rect = el.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX
    };
  }
  setPosition(): void {
    const divElRef = this.divElRef.nativeElement;
    const alignHeight = this.dropDownContainer.elementRef.nativeElement.offsetHeight + 50;
    const isBottom = this.settings.position === 'bottom'; 
    this.renderer.setStyle(divElRef, 'position', 'relative');
    const position = isBottom ? -7 : alignHeight;
    const positionProperty = isBottom ? 'top' : 'bottom';
    this.renderer.setStyle(divElRef, positionProperty, position + 'px');
  }
}
